import {
  HOUR,
  isEmpty,
  messageFromError,
  type APIError,
  type ProgramId,
  type ProgramUserListInput,
  type ProgramUserListSuccess,
} from '@terros/common'
import { useInfiniteQuery, type InfiniteData, type QueryKey, type UseInfiniteQueryResult } from '@tanstack/react-query'
import { useServerRequest } from '../api'
import { programUserInfiniteKey } from './keys'

const PAGE_SIZE = 50

export type UseInfiniteProgramUsersResult = Omit<
  UseInfiniteQueryResult<InfiniteData<ProgramUserListSuccess>>,
  'error'
> & {
  error?: string
}

export function useInfiniteProgramUsers(
  programId: ProgramId,
  input?: Omit<ProgramUserListInput, 'programId'>
): UseInfiniteProgramUsersResult {
  const serverRequest = useServerRequest<ProgramUserListInput, ProgramUserListSuccess>('/program/user/list')
  const query = useInfiniteQuery<
    ProgramUserListSuccess,
    APIError,
    InfiniteData<ProgramUserListSuccess>,
    QueryKey,
    ProgramUserListInput
  >({
    queryKey: programUserInfiniteKey(programId, input),
    queryFn: async ({ pageParam }) => serverRequest(pageParam),
    initialPageParam: { programId, ...input, size: PAGE_SIZE },
    getNextPageParam: (lastPage) => {
      if (isEmpty(lastPage.users) || !lastPage.sortTimestamp) return
      return {
        programId,
        ...input,
        size: PAGE_SIZE,
        sortTimestamp: lastPage.sortTimestamp,
      }
    },
    staleTime: HOUR,
    gcTime: 6 * HOUR,
  })
  const error = messageFromError(query.error)

  return {
    ...query,
    error,
  }
}
